import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import { isNotEmpty, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { createCoupon } from '../coupon-list/core/_requests'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../auth'
import { AlertContext } from '../../../../../_metronic/layout/components/alert-context'
import { useQueryResponse } from '../coupon-list/core/QueryResponseProvider'
import { getExCRUD } from '../../role-access/role-setting-list/core/_requests'
import { getPlan } from '../../users/user-list/core/_requests'

export interface ICouponDetails {
  code: string
  description: string
  discount_type: string
  discount_value: number
  min_purchase_amount: number
  max_discount_amount: number
  start_date: string
  end_date: string
  usage_limit: number
  status: number
  coupon_type: number
  plan_id?: number | string
  createdBy?: string | undefined
}

export let initialValues: ICouponDetails = {
  code: '',
  description: '',
  discount_type: 'Percentage',
  discount_value: 0,
  min_purchase_amount: 0,
  max_discount_amount: 0,
  start_date: new Date().toISOString().split('T')[0],
  end_date: new Date().toISOString().split('T')[0],
  usage_limit: 0,
  status: 1,
  coupon_type: 1,
  plan_id: '',
}

const couponDetailsSchema = Yup.object().shape({
  code: Yup.string().required('Coupon Code is required'),
  description: Yup.string(),
  discount_type: Yup.string().required('Discount Type is required'),
  discount_value: Yup.number().required('Discount Value is required').min(0, 'Value must be at least 0'),
  min_purchase_amount: Yup.number().required('Min Purchase Amount is required').min(0, 'Must be at least 0'),
  max_discount_amount: Yup.number().required('Max Discount Amount is required').min(0, 'Must be at least 0'),
  start_date: Yup.date().required('Start Date is required'),
  end_date: Yup.date().required('End Date is required').min(Yup.ref('start_date'), "End Date cannot be before Start Date"),
  usage_limit: Yup.number().required('Usage Limit is required').min(0, 'Must be at least 0'),
  coupon_type: Yup.number().required('Coupon Type is required'),
})

const AddCoupon: React.FC = () => {
  const { showAlert } = useContext(AlertContext)
  const { refetch } = useQueryResponse()
  const [data, setData] = useState<ICouponDetails>({ ...initialValues })
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [plans, setPlans] = useState<any[]>([])

  const [loading, setLoading] = useState(false)
  const formik = useFormik<ICouponDetails>({
    initialValues,
    validationSchema: couponDetailsSchema,
    onSubmit: (values, { resetForm }) => {
      setLoading(true)
      const finalDescription = values.description
        ? `${values.description}`
        : `${values.code} created`

      const updatedData = { ...data, ...values, description: finalDescription }
      updatedData.createdBy = currentUser?.userId

      createCoupon(updatedData).then((res) => {
        resetForm({
          values: { ...initialValues },
        })
        setData({ ...initialValues })
        navigate('/coupon', { replace: true })
        refetch()
        setLoading(false)
        showAlert(res?.Message, res?.Code)
      }).catch((err: any) => {
        setLoading(false)
        if (err.response && err.response.data && err.response.data.Message) {
          showAlert(err.response.data.Message, err.response.data.Code || '0')
        } else {
          showAlert('Error creating coupon', '0')
        }
      })
    },
    enableReinitialize: true,
  })

  const [roleaccess, setroleaccess] = useState<any>([])
  useEffect(() => {
    getExCRUD(currentUser?.roleId, 5).then((res: any) => {
      var crudres = res.data?.Data[0]
      setroleaccess(crudres)
    })

    getPlan().then((res: any) => {
      if (res.data?.Data) {
        setPlans(res.data.Data)
      }
    }).catch((err: any) => {
      console.error("Error fetching plans", err)
    })
  }, [])

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0'
        aria-expanded='true'
        aria-controls='kt_account_profile_details'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Add Coupon </h3>
        </div>
      </div>

      <div id='kt_account_profile_details'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>

            <div className='row mb-6'>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Coupon Type</label>
                <select
                  className='form-select form-select-lg form-select-solid'
                  {...formik.getFieldProps('coupon_type')}
                  onChange={(e) => {
                    formik.setFieldValue('coupon_type', Number(e.target.value))
                  }}
                >
                  <option value={1}>Common</option>
                  <option value={2}>Plan Specific</option>
                  <option value={3}>Promoter</option>
                </select>
                {formik.touched.coupon_type && formik.errors.coupon_type && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.coupon_type}</div>
                  </div>
                )}
              </div>
              {Number(formik.values.coupon_type) === 2 && (
                <div className='col-lg-6 fv-row'>
                  <label className='col-form-label required fw-bold fs-6'>Select Plan</label>
                  <Select
                    isMulti
                    className='react-select-container'
                    classNamePrefix='react-select'
                    options={plans.filter((p: any) => p.amount > 0).map((plan: any) => ({ value: String(plan.id), label: plan.name }))}
                    value={
                      formik.values.plan_id
                        ? String(formik.values.plan_id).split(',').filter(Boolean).map(id => {
                            const plan = plans.find((p: any) => String(p.id) === String(id));
                            return { value: String(id), label: plan ? plan.name : id };
                          })
                        : []
                    }
                    onChange={(selectedOptions: any) => {
                      const selectedValues = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
                      formik.setFieldValue('plan_id', selectedValues.join(','));
                    }}
                    placeholder='Select Plan(s)...'
                  />
                  {formik.touched.plan_id && formik.errors.plan_id && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>{formik.errors.plan_id}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='row mb-6'>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Code</label>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  placeholder='e.g., SUMMER10'
                  {...formik.getFieldProps('code')}
                />
                {formik.touched.code && formik.errors.code && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.code}</div>
                  </div>
                )}
              </div>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Discount Type</label>
                <select
                  className='form-select form-select-lg form-select-solid'
                  {...formik.getFieldProps('discount_type')}
                >
                  <option value='Percentage'>Percentage</option>
                  <option value='Fixed'>Fixed Amount</option>
                </select>
                {formik.touched.discount_type && formik.errors.discount_type && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.discount_type}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Discount Value</label>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  placeholder='Enter Value'
                  {...formik.getFieldProps('discount_value')}
                />
                {formik.touched.discount_value && formik.errors.discount_value && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.discount_value}</div>
                  </div>
                )}
              </div>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Usage Limit</label>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  placeholder='Usage Limit'
                  {...formik.getFieldProps('usage_limit')}
                />
                {formik.touched.usage_limit && formik.errors.usage_limit && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.usage_limit}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Min Purchase Amount</label>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  placeholder='Min Purchase Amount'
                  {...formik.getFieldProps('min_purchase_amount')}
                />
                {formik.touched.min_purchase_amount && formik.errors.min_purchase_amount && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.min_purchase_amount}</div>
                  </div>
                )}
              </div>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Max Discount Amount</label>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  placeholder='Max Discount Amount'
                  {...formik.getFieldProps('max_discount_amount')}
                />
                {formik.touched.max_discount_amount && formik.errors.max_discount_amount && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.max_discount_amount}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>Start Date</label>
                <input
                  type='date'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  {...formik.getFieldProps('start_date')}
                />
                {formik.touched.start_date && formik.errors.start_date && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.start_date as string}</div>
                  </div>
                )}
              </div>
              <div className='col-lg-6 fv-row'>
                <label className='col-form-label required fw-bold fs-6'>End Date</label>
                <input
                  type='date'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  {...formik.getFieldProps('end_date')}
                />
                {formik.touched.end_date && formik.errors.end_date && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.end_date as string}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <div className='col-lg-12 fv-row'>
                <label className='col-form-label fw-bold fs-6'>Description</label>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Enter Coupon Description'
                  {...formik.getFieldProps('description')}
                />
              </div>
            </div>

          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            {roleaccess?.add === 1 && (
              <button type='submit' className='btn btn-primary' disabled={loading}>
                {!loading && 'Save Changes'}
                {loading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddCoupon
